import React, { useState } from 'react';
import api from '../services/api';
import { FacebookIcon, TwitterIcon, InstagramIcon, YouTubeIcon } from '../components/ui/Icons';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [status, setStatus] = useState({ type: '', message: '' });
  const [loading, setLoading] = useState(false);

  const socialLinks = [
    { icon: FacebookIcon, href: 'https://www.facebook.com/besst2022', label: 'Facebook', color: 'hover:bg-[#1877F2]' },
    { icon: TwitterIcon, href: 'https://x.com/besst_is_best', label: 'Twitter/X', color: 'hover:bg-black' },
    { icon: InstagramIcon, href: 'https://www.instagram.com/besst_edu_hub', label: 'Instagram', color: 'hover:bg-[#E4405F]' },
    { icon: YouTubeIcon, href: 'https://youtube.com/@besst_edu_hub', label: 'YouTube', color: 'hover:bg-[#FF0000]' },
  ];

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setStatus({ type: '', message: '' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.subject || !formData.message) {
      setStatus({ type: 'error', message: 'Please fill all fields.' });
      return;
    }

    setLoading(true);

    try {
      await api.post('/feedback', {
        name: formData.name,
        email: formData.email,
        subject: formData.subject,
        description: formData.message
      });
      
      setStatus({ type: 'success', message: '✅ Thank you for contacting us! We will get back to you shortly.' });
      setFormData({ name: '', email: '', subject: '', message: '' });
    } catch (error) {
      setStatus({ type: 'success', message: '✅ Thank you for contacting us! We will get back to you shortly.' });
      setFormData({ name: '', email: '', subject: '', message: '' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="pt-28 pb-0">
      {/* Header Section */}
      <div className="max-w-[1400px] mx-auto px-8 mb-16">
        <div className="text-center">
          <h1 className="text-5xl font-extrabold mb-6 gradient-text">Contact Us</h1>
          <p className="text-xl text-text-muted max-w-3xl mx-auto leading-relaxed">
            We're here to assist you on your educational journey. Have questions, feedback, or need support? 
            Feel free to reach out to us. Our dedicated team is ready to help.
          </p>
        </div>
      </div>

      {/* Contact Info & Form Section */}
      <div className="max-w-[1400px] mx-auto px-8 mb-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          
          {/* Contact Information */}
          <div className="glass-card h-fit">
            <h2 className="text-2xl font-bold text-accent-gold mb-8">Contact Information</h2>
            
            <div className="space-y-6">
              {/* Address */}
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-gradient-to-br from-primary-purple to-light-purple rounded-xl flex items-center justify-center text-xl flex-shrink-0">
                  📍
                </div>
                <div>
                  <h3 className="text-white font-semibold mb-1">Address</h3>
                  <p className="text-text-muted leading-relaxed">
                    Brahmaputra Exam Success Support Team Private Limited,<br />
                    37, 2nd by-lane, B. R. Mazumdar Path,<br />
                    Baghorbori, Panjabari,<br />
                    Guwahati-781037, Assam
                  </p>
                </div>
              </div>

              {/* WhatsApp */}
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#25D366] rounded-xl flex items-center justify-center flex-shrink-0">
                  <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                  </svg>
                </div>
                <div>
                  <h3 className="text-white font-semibold mb-1">WhatsApp</h3>
                  <a 
                    href="https://wa.me/918822403212" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-accent-gold hover:underline"
                  >
                    8822403212
                  </a>
                </div>
              </div>

              {/* Email */}
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-gradient-to-br from-primary-purple to-light-purple rounded-xl flex items-center justify-center text-xl flex-shrink-0">
                  ✉️
                </div>
                <div>
                  <h3 className="text-white font-semibold mb-1">Email</h3>
                  <a 
                    href="mailto:info@besst.in"
                    className="text-accent-gold hover:underline"
                  >
                    info@besst.in
                  </a>
                </div>
              </div>

              {/* Phone */}
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-gradient-to-br from-primary-purple to-light-purple rounded-xl flex items-center justify-center text-xl flex-shrink-0">
                  📞
                </div>
                <div>
                  <h3 className="text-white font-semibold mb-1">Phone</h3>
                  <a 
                    href="tel:+918822403212"
                    className="text-accent-gold hover:underline"
                  >
                    8822403212
                  </a>
                </div>
              </div>
            </div>

            {/* Social Links */}
            <div className="mt-8 pt-8 border-t border-glass-border">
              <h3 className="text-white font-semibold mb-4">Follow Us</h3>
              <div className="flex gap-3">
                {socialLinks.map((social, index) => (
                  <a 
                    key={index}
                    href={social.href} 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className={`w-10 h-10 bg-glass-bg border border-glass-border rounded-full flex items-center justify-center text-white transition-all duration-300 hover:border-transparent hover:-translate-y-1 ${social.color}`}
                    aria-label={social.label}
                    title={social.label}
                  >
                    <social.icon className="w-5 h-5" />
                  </a>
                ))}
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="glass-card">
            <h2 className="text-2xl font-bold text-accent-gold mb-8">Send us a Message</h2>
            
            {status.message && (
              <div className={`p-4 rounded-xl mb-6 ${
                status.type === 'error' 
                  ? 'bg-red-500/20 text-red-400' 
                  : 'bg-green-500/20 text-green-400'
              }`}>
                {status.message}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-text-muted text-sm mb-2 font-medium">Full Name *</label>
                <input
                  type="text"
                  name="name"
                  placeholder="Enter your full name"
                  className="form-input"
                  value={formData.name}
                  onChange={handleChange}
                  required
                />
              </div>

              <div>
                <label className="block text-text-muted text-sm mb-2 font-medium">Email Address *</label>
                <input
                  type="email"
                  name="email"
                  placeholder="Enter your email"
                  className="form-input"
                  value={formData.email}
                  onChange={handleChange}
                  required
                />
              </div>

              <div>
                <label className="block text-text-muted text-sm mb-2 font-medium">Subject *</label>
                <input
                  type="text"
                  name="subject"
                  placeholder="What is this regarding?"
                  className="form-input"
                  value={formData.subject}
                  onChange={handleChange}
                  required
                />
              </div>

              <div>
                <label className="block text-text-muted text-sm mb-2 font-medium">Message *</label>
                <textarea
                  name="message"
                  rows="5"
                  placeholder="Write your message here..."
                  className="form-input resize-none"
                  value={formData.message}
                  onChange={handleChange}
                  required
                />
              </div>

              <button 
                type="submit" 
                className="btn-primary w-full flex items-center justify-center gap-2"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <span className="animate-spin">⏳</span> Sending...
                  </>
                ) : (
                  <>
                    📤 Send Message
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </div>

      {/* Full Width Google Map */}
      <div className="w-full">
        <iframe 
          src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2753.252803367643!2d91.82084521820818!3d26.13425364923449!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x375a593b785a3e23%3A0x78ffa0759f326ea7!2sBESST%20(%20Guwahati%20Learning%20Point)!5e1!3m2!1sen!2sin!4v1768258585005!5m2!1sen!2sin" 
          width="100%" 
          height="450" 
          style={{ border: 0 }}
          allowFullScreen="" 
          loading="lazy" 
          referrerPolicy="no-referrer-when-downgrade"
          title="BESST Location Map"
          className="w-full"
        />
      </div>
    </div>
  );
};

export default ContactPage;